import { memo } from 'preact/compat'
import { useState } from 'preact/hooks'
import type { DashboardNewestConversionItemDto } from '@/types/dashboard'
import { Clock } from 'lucide-preact'
import { shortAgo } from '@/utils/time'
import { rowActivation } from '@/utils/rowActivation'
import { conversionAnchorLeadId } from '@/utils/dashboardRowTargets'
import { PersonAvatar } from '@/components/common/Avatar'
import { ContactPeekFlyout } from '@/components/contacts/ContactPeekFlyout'

interface NewestConversionsTileProps {
    conversions: DashboardNewestConversionItemDto[] | null | undefined
    clientCompanyId: string
    workspaceId: string
    /** Present on the campaign tab, absent on the workspace dashboard. Decides which profile opens. */
    campaignId?: string
    limit?: number
    className?: string
    loading?: boolean
    error?: string | null
    /** The server could not compute this section — shown as broken, not as empty. */
    failed?: boolean
    /** Suppresses the flyout while the dashboard is showing mock data. */
    mock?: boolean
}

export const NewestConversionsTile = memo(({ conversions, clientCompanyId, workspaceId, campaignId, limit = 5, className = '', loading = false, error = null, failed = false, mock = false }: NewestConversionsTileProps) => {
    const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)

    return (
        <div className={`tile flex flex-col ${className}`.trim()}>
            <div className="flex items-start justify-between mb-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
                    Newest Conversions
                </h3>
                <Clock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            {loading && (
                <ul className="space-y-3 mt-1">
                    {Array.from({ length: limit }).map((_, i) => (
                        <li key={i} className="flex items-center gap-3 row-hover">
                            <div className="w-10 h-10 rounded-full bg-shimmer-light animate-shimmer" />
                            <div className="flex-1 space-y-2">
                                <div className="h-3 w-32 rounded bg-shimmer-light animate-shimmer" />
                                <div className="h-3 w-20 rounded bg-shimmer-light animate-shimmer" />
                            </div>
                            <div className="h-3 w-12 rounded bg-shimmer-light animate-shimmer" />
                        </li>
                    ))}
                </ul>
            )}
            {!loading && error && (
                <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
            {!loading && !error && failed && (
                <div className="text-sm text-red-600 dark:text-red-400">Couldn't load newest conversions</div>
            )}
            {!loading && !error && !failed && conversions && conversions.length === 0 && (
                <div className="text-sm text-slate-500 dark:text-slate-400">No recent conversions</div>
            )}
            {!loading && !error && !failed && conversions && conversions.length > 0 && (
                <ul className="space-y-3 mt-1">
                    {conversions.map((c: DashboardNewestConversionItemDto) => {
                        // Without a client company the flyout loads but its "Open full profile"
                        // would build a route with an empty segment. Same guard the web tile carries.
                        const canOpen = !mock && !!clientCompanyId && !!workspaceId
                        const leadId = canOpen ? conversionAnchorLeadId(c, campaignId) : null
                        const activation = rowActivation(leadId ? () => setSelectedLeadId(leadId) : null)
                        return (
                            <li key={c.LeadId}>
                                <div
                                    {...activation}
                                    className={`flex items-center gap-3 rounded-md p-2 -m-2 ${activation ? 'row-hover cursor-pointer' : ''}`}
                                >
                                    <PersonAvatar
                                        firstName={c.FirstName}
                                        lastName={c.LastName}
                                        fullName={c.Name}
                                        profilePicUrl={c.ProfileImageUrl}
                                        size="lg"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={c.Name || undefined}>{c.Name || 'Unknown Contact'}</div>
                                        <div className="text-2xs uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate" title={c.CompanyName || undefined}>{c.CompanyName || '—'}</div>
                                    </div>
                                    <div className="text-2xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap" title={new Date(c.ConvertedAtUtc).toLocaleString()}>
                                        {shortAgo(c.ConvertedAtUtc)}
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
            {selectedLeadId && (
                <ContactPeekFlyout
                    anchorType="lead"
                    anchorId={selectedLeadId}
                    clientCompanyId={clientCompanyId}
                    workspaceId={workspaceId}
                    campaignId={campaignId}
                    onClose={() => setSelectedLeadId(null)}
                />
            )}
        </div>
    )
})
